/**
 * ╔════════════════════════════════════════════════════════════════════════════╗
 * ║  ⚠️  CRITICAL PWA FILE - DO NOT MODIFY WITHOUT CAREFUL CONSIDERATION  ⚠️   ║
 * ╠════════════════════════════════════════════════════════════════════════════╣
 * ║  This file controls the PWA install functionality.                         ║
 * ║  Changes to this file can break the app installation on Android/iOS.       ║
 * ║                                                                            ║
 * ║  BEFORE MAKING ANY CHANGES:                                                ║
 * ║  1. Read /app/memory/PWA_PROTECTED.md                                      ║
 * ║  2. Test thoroughly on real Android devices                                ║
 * ║  3. Ensure beforeinstallprompt event still fires                           ║
 * ║                                                                            ║
 * ║  Last verified working: April 7, 2026                                      ║
 * ╚════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';
import { topModalCoordinator } from '../lib/topModalCoordinator';

export function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed (multiple detection methods)
    const checkIfInstalled = () => {
      // Method 1: Standalone mode detection (PWA is running)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
        || window.navigator.standalone === true
        || document.referrer.includes('android-app://');
      
      // Method 2: Check localStorage for previous installation
      const wasInstalled = localStorage.getItem('pwa-installed') === 'true';
      
      // Method 3: Check if user previously dismissed the prompt
      const wasDismissed = localStorage.getItem('pwa-prompt-dismissed') === 'true';
      
      return isStandalone || wasInstalled || wasDismissed;
    };

    // If already installed or dismissed, don't show prompt at all
    if (checkIfInstalled()) {
      setIsInstalled(true);
      return;
    }

    // Show the prompt after a short delay (only if not installed)
    const timer = setTimeout(() => {
      // Double-check installation status before showing
      if (!checkIfInstalled()) {
        setShowPrompt(true);
      }
    }, 2000);

    // Listen for beforeinstallprompt event (for native install)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      // Persist installation state
      localStorage.setItem('pwa-installed', 'true');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    // Listen for display mode changes (e.g., when app is launched as PWA)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      if (e.matches) {
        localStorage.setItem('pwa-installed', 'true');
        setIsInstalled(true);
        setShowPrompt(false);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Use native install prompt if available
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        // Persist installation state
        localStorage.setItem('pwa-installed', 'true');
        setShowPrompt(false);
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // Show manual instructions - also mark as dismissed after showing
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      
      if (isIOS) {
        alert('To install:\n1. Tap the Share button (square with arrow)\n2. Scroll down and tap "Add to Home Screen"');
      } else if (isAndroid) {
        alert('To install:\n1. Tap the menu button (⋮) in your browser\n2. Tap "Add to Home Screen" or "Install App"');
      } else {
        alert('To install:\nLook for "Install" or "Add to Home Screen" in your browser menu');
      }
      // After showing instructions, hide the prompt (user knows how to install)
      localStorage.setItem('pwa-prompt-dismissed', 'true');
      setShowPrompt(false);
      setIsInstalled(true);
    }
  };

  const handleDismiss = () => {
    // Persist dismissal so prompt doesn't show again
    localStorage.setItem('pwa-prompt-dismissed', 'true');
    setShowPrompt(false);
    setIsInstalled(true); // Treat as "handled" so it won't show again
  };

  // Publish the prompt's visibility so other top-level modals can defer
  // opening and avoid stacking / pointer-events conflicts.
  useEffect(() => {
    const visible = !isInstalled && showPrompt;
    if (visible) topModalCoordinator.open('install');
    else topModalCoordinator.close('install');
    return () => topModalCoordinator.close('install');
  }, [isInstalled, showPrompt]);

  // Don't show if already installed or prompt not ready
  if (isInstalled || !showPrompt) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div className="relative bg-gradient-to-br from-emerald-600 to-teal-600 text-white rounded-2xl shadow-2xl max-w-sm w-full p-6 flex flex-col items-center gap-4 animate-in zoom-in-95 duration-300">
        <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center">
          <Download className="h-6 w-6" />
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold">Install Jigsaw Blocks</div>
          <div className="text-sm opacity-80 mt-1">
            Add to your home screen for the best experience
          </div>
        </div>
        <div className="flex gap-2 w-full mt-2">
          <Button
            variant="secondary"
            onClick={handleDismiss}
            className="flex-1 bg-white/10 text-white hover:bg-white/20 border border-white/20"
            data-testid="pwa-install-dismiss-btn"
          >
            Later
          </Button>
          <Button
            onClick={handleInstall}
            className="flex-1 bg-white text-emerald-700 hover:bg-emerald-50"
            data-testid="pwa-install-btn"
          >
            Install
          </Button>
        </div>
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
